import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { MapPin, Navigation, Store, Heart, Clock, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api, img, errMsg, viewerId } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useLoc } from "@/lib/location";
import { dist, money, offerText, fmtDate, OFFER_TYPES } from "@/lib/format";
import { haversine } from "@/lib/geo";
import { Navbar, Footer } from "@/components/site/Navbar";
import { Countdown } from "@/components/site/bits";
import { ShareMenu } from "@/components/site/ShareMenu";
import { DealCard } from "@/components/site/DealCard";
import MapView from "@/components/site/MapView";

function Gallery({ images, title }) {
  const [i, setI] = useState(0);
  return (
    <div className="space-y-3">
      <div className="aspect-[4/3] rounded-3xl overflow-hidden bg-slate-100">{images[i] && <img data-testid="deal-gallery-main" src={img(images[i])} alt={title} className="w-full h-full object-cover" />}</div>
      {images.length > 1 && (
        <div className="grid grid-cols-5 gap-2">
          {images.map((u, k) => (
            <button key={u} data-testid={`deal-gallery-thumb-${k}`} onClick={() => setI(k)} className={`aspect-square rounded-xl overflow-hidden border-2 ${k === i ? "border-red-500" : "border-transparent"}`}><img src={img(u)} alt="" className="w-full h-full object-cover" /></button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function DealDetail() {
  const { slug } = useParams();
  const [sp] = useSearchParams();
  const { user } = useAuth();
  const { loc } = useLoc();
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  const [claiming, setClaiming] = useState(false);
  const [fav, setFav] = useState(false);
  const autoClaimed = useRef(false);

  useEffect(() => {
    setD(null);
    api.get(`/deals/${slug}`).then((r) => {
      setD(r.data);
      document.title = `${r.data.title} — ${offerText(r.data)} | SuperDeals.world`;
      api.post(`/deals/${r.data.id}/view`, {}, { headers: { "X-Viewer-Id": viewerId() } }).catch(() => {});
    }).catch(() => setErr("This deal could not be found."));
  }, [slug]);
  useEffect(() => {
    if (user?.role === "customer" && d) api.get("/customer/favorites").then((r) => setFav(r.data.deal_ids.includes(d.id))).catch(() => {});
  }, [user, d]);

  const claim = async () => {
    if (!user) return nav(`/login?next=${encodeURIComponent(`/deals/${slug}?claim=1`)}`);
    if (user.role !== "customer") return toast.error("Log in with a customer account to claim deals.");
    setClaiming(true);
    try {
      const { data } = await api.post(`/deals/${d.id}/claim`);
      toast.success("Coupon claimed!");
      nav(`/customer/coupons/${data.id}`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setClaiming(false);
    }
  };
  useEffect(() => {
    if (d && d.is_live && sp.get("claim") === "1" && user?.role === "customer" && !autoClaimed.current) {
      autoClaimed.current = true;
      claim();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [d, user]);

  const toggleFav = async () => {
    if (user?.role !== "customer") return nav(`/login?next=/deals/${slug}`);
    await api.post(fav ? "/customer/favorites/remove" : "/customer/favorites", { deal_id: d.id });
    setFav(!fav);
  };

  if (err) return <div><Navbar /><p data-testid="deal-not-found" className="p-16 text-center text-slate-500">{err}</p></div>;
  if (!d) return <div><Navbar /><div className="max-w-7xl mx-auto p-8"><div className="h-96 rounded-3xl bg-slate-100 animate-pulse" /></div></div>;

  const km = haversine(loc.lat, loc.lng, d.lat, d.lng);
  return (
    <div className="bg-white">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-7"><Gallery images={d.images || []} title={d.title} /></div>
        <div className="lg:col-span-5 space-y-5">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="px-3 py-1 rounded-full text-sm font-extrabold bg-gradient-to-r from-red-600 to-orange-500 text-white">{offerText(d)}</span>
            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{d.category_name} · {OFFER_TYPES[d.offer_type]}</span>
          </div>
          <h1 data-testid="deal-title" className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">{d.title}</h1>
          <div className="flex items-center gap-2 text-slate-600"><Store className="w-4 h-4" /><b>{d.merchant_name}</b> · {d.branch_name}</div>
          <div className="flex items-end gap-3">
            {d.offer_price != null && <span data-testid="deal-offer-price" className="font-heading text-4xl font-extrabold text-slate-900">{money(d.offer_price)}</span>}
            {d.original_price != null && <span className="text-lg text-slate-400 line-through">{money(d.original_price)}</span>}
          </div>
          <div className="rounded-2xl bg-amber-50 border border-amber-200 p-4 flex items-center justify-between">
            <div className="text-sm text-amber-900"><Clock className="inline w-4 h-4 mr-1" />{d.is_live ? "Deal ends in" : "Deal status"}<span className="block text-xs text-amber-700">Ends {fmtDate(d.expires_at, d.timezone)}</span></div>
            {d.is_live ? <Countdown to={d.expires_at} className="text-base px-3 py-1.5" testid="deal-countdown" /> : <span data-testid="deal-unavailable" className="font-bold text-slate-600">No longer available</span>}
          </div>
          <div className="flex gap-3">
            <button data-testid="claim-deal-button" disabled={!d.is_live || claiming} onClick={claim}
              className="flex-1 h-14 rounded-2xl bg-red-500 hover:bg-red-600 disabled:bg-slate-300 text-white text-lg font-extrabold tracking-wide active:scale-[0.98] transition-transform flex items-center justify-center gap-2">
              {claiming && <Loader2 className="w-5 h-5 animate-spin" />} CLAIM DEAL
            </button>
            <button data-testid="favorite-button" onClick={toggleFav} className="w-14 h-14 rounded-2xl border border-slate-200 grid place-items-center hover:bg-slate-50"><Heart className={`w-5 h-5 ${fav ? "fill-red-500 text-red-500" : ""}`} /></button>
            <ShareMenu deal={d} className="!w-14 !h-14 !rounded-2xl" testid="deal-share-button" />
          </div>
          <p className="text-xs text-slate-500 flex items-center gap-1"><ShieldCheck className="w-4 h-4 text-emerald-600" /> Coupon valid 24 hours after claiming or until the deal ends, whichever is earlier.{d.max_claims ? ` ${Math.max(0, d.max_claims - d.claims_count)} of ${d.max_claims} left.` : ""}</p>
          <div><h2 className="font-heading font-bold text-slate-900 mb-1">About this deal</h2><p className="text-slate-600 whitespace-pre-line">{d.description}</p></div>
          {d.terms && <div><h2 className="font-heading font-bold text-slate-900 mb-1">Terms & conditions</h2><p className="text-sm text-slate-600 whitespace-pre-line">{d.terms}</p></div>}
          {d.redemption_instructions && <div><h2 className="font-heading font-bold text-slate-900 mb-1">How to redeem</h2><p className="text-sm text-slate-600">{d.redemption_instructions}</p></div>}
        </div>
        <div className="lg:col-span-12 grid lg:grid-cols-12 gap-6">
          <div className="lg:col-span-8"><MapView lat={d.lat} lng={d.lng} label={d.branch_name} height={320} /></div>
          <div className="lg:col-span-4 border border-slate-200 rounded-2xl p-5 space-y-3">
            <p className="font-heading font-bold text-slate-900">{d.branch_name}</p>
            <p className="text-sm text-slate-600 flex gap-2"><MapPin className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />{d.branch_address}</p>
            <p data-testid="deal-distance" className="text-sm"><b>{dist(km * 1000)}</b> from {loc.label}</p>
            {d.branch?.opening_hours && <p className="text-sm text-slate-600">Hours: {d.branch.opening_hours}</p>}
            <a data-testid="directions-button" href={`https://www.google.com/maps/dir/?api=1&destination=${d.lat},${d.lng}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-900 text-white text-sm font-semibold"><Navigation className="w-4 h-4" /> Get directions</a>
          </div>
        </div>
      </div>
      <Related deal={d} />
      <Footer />
    </div>
  );
}

function Related({ deal }) {
  const { loc } = useLoc();
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get("/deals", { params: { lat: deal.lat, lng: deal.lng, radius_km: 10, exclude: deal.id, limit: 4 } }).then((r) => setItems(r.data)).catch(() => {});
  }, [deal, loc]);
  if (!items.length) return null;
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="related-deals">
      <h2 className="font-heading text-2xl font-bold text-slate-900 mb-5">More deals nearby</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">{items.map((x, i) => <DealCard key={x.id} deal={x} index={i} />)}</div>
      <Link to="/deals" className="sr-only">All deals</Link>
    </section>
  );
}
